import { last } from "@prismicio/editor-support/Array"
import { map } from "@prismicio/editor-support/Set"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import type { FilterOption } from "@prismicio/editor-ui"
import { useState } from "react"

import { searchDocumentStatus } from "../../../core/document"
import {
  createSearchParams,
  type DocumentMeta,
  type SearchDocumentNames,
  searchDocuments
} from "../../../core/service"
import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig
} from "../../../EditorConfig"

export function useDocuments(params: {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
}) {
  const { searchTerm, tagsFilter, typesFilter, localesFilter } = params
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { coreApiBaseUrl, authStrategy, status } = config

  const [cursor, setCursor] = useState<string>("")
  const [documents, setDocuments] = useState<DocumentMeta[]>([])
  const [previousQueryString, setPreviousQueryString] = useState("")

  const queryString = new URLSearchParams(
    createSearchParams<SearchDocumentNames>([
      { name: "tags", values: [...map(tagsFilter, (tag) => tag.id)] },
      { name: "customTypes", values: [...map(typesFilter, (type) => type.id)] },
      {
        name: "language",
        values: [...map(localesFilter, (locale) => locale.id)]
      },
      { name: "status", values: searchDocumentStatus[status] },
      { name: "term", values: [searchTerm] }
    ])
  ).toString()

  const response = useRequest(searchLinkDocuments, [
    coreApiBaseUrl,
    queryString === previousQueryString ? cursor : "", // The cursor needs to be updated immediately for this request
    authStrategy,
    queryString
  ])

  if (queryString !== previousQueryString) {
    // The search term or filters have changed, so the state should reset to avoid
    // duplicate documents in the list.
    setPreviousQueryString(queryString)
    setCursor("")
    setDocuments(response.results)
  } else if (
    response.results.length > 0 &&
    last(documents)?.id !== last(response.results)?.id
  ) {
    setDocuments((currDocuments) => currDocuments.concat(response.results))
  }

  const hasNextPage = response.total !== documents.length

  function fetchNextPage() {
    if (response.cursor === undefined) return

    setCursor(response.cursor)
  }

  return {
    documents,
    hasNextPage,
    fetchNextPage,
    response
  }
}

// Don't need our cache to re-evaluate whenever we change browser tab.
// The cache is already updated based on users scrolling to the bottom & searching
cache.registerFetcherConfig(searchLinkDocuments, {
  cacheCapacity: 500,
  revalidationStrategy: { type: "never" }
})

function searchLinkDocuments(
  baseUrl: URL,
  cursor: string,
  authStrategy: AuthStrategy,
  searchTerm: string
) {
  const limit = 30
  return searchDocuments(baseUrl, authStrategy, searchTerm, limit, cursor)
}
